import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { usePublicBillStatementLazyQuery } from 'apollo/queries/autogenerate/hooks-public';
import { billStatementFragment } from 'apollo/queries/autogenerate/operations';
import { BillStatementStatus } from 'apollo/queries/autogenerate/schemas-public';
import ImageCheck from 'shared/assets/images/check@3x.png';
import Logo from 'shared/assets/images/logo.png';
import { COMBINI_FEE } from 'shared/constants/fees';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import generateID from 'shared/modules/common/utils/generate-id';
import BankTransfer from 'shared/modules/settlement/components/organisms/BankTransfer';
import CombiniSettlement, {
  combiniData,
} from 'shared/modules/settlement/components/organisms/CombiniSettlement';
import SettlementMethods from 'shared/modules/settlement/components/organisms/SettlementMethods';
import { getNumberOfDaysToNow } from 'shared/utils/date';
import { formatToCurrency, formatYearMonthDate } from 'shared/utils/japanese';
import Mixpanel from 'shared/vendor/Mixpanel';
import { ACTION, SCREEN } from 'web/modules/settlement/constants/mixpanel';
import routes from 'web/navigation/routes';

const PAGE_VIEW_UNAUTHENTICATED_BILL = `${SCREEN.UNAUTHENTICATED_BILL} - ${ACTION.PAGE_VIEW}`;
const TIMED_PAGE_VIEW_UNAUTHENTICATED_BILL = `Timed ${SCREEN.UNAUTHENTICATED_BILL} - ${ACTION.PAGE_VIEW}`;
const PAGE_VIEW_UNAUTHENTICATED_BILL_UNAVAILABLE = `${SCREEN.UNAUTHENTICATED_BILL_UNAVAILABLE} - ${ACTION.PAGE_VIEW}`;
const PAGE_VIEW_UNAUTHENTICATED_PAID_BILL = `${SCREEN.UNAUTHENTICATED_PAID_BILL} - ${ACTION.PAGE_VIEW}`;
const PAGE_VIEW_VAN_ERROR = `${SCREEN.UNAUTHENTICATED_VAN_ERROR} - ${ACTION.PAGE_VIEW}`;

const PAGE_VIEW_SETTLEMENT: any = {
  loppi: `${SCREEN.UNAUTHENTICATED_LAWSON_MINISTOP_SETTLEMENT} - ${ACTION.PAGE_VIEW}`,
  familymart: `${SCREEN.UNAUTHENTICATED_FAMILYMART_SETTLEMENT} - ${ACTION.PAGE_VIEW}`,
  seicomart: `${SCREEN.UNAUTHENTICATED_SEICOMART_SETTLEMENT} - ${ACTION.PAGE_VIEW}`,
  transfer: `${SCREEN.UNAUTHENTICATED_VAN_SETTLEMENT} - ${ACTION.PAGE_VIEW}`,
};

export const NavigationBar = () => {
  const navigate = useNavigate();
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  return (
    <View
      accessibilityRole="navigation"
      style={[
        style.flexHorizontal,
        style.nav,
        isBigScreen && style.navBigScreen,
      ]}
    >
      <View style={style.flexHorizontal}>
        <Image
          source={{ uri: Logo }}
          style={{
            width: isBigScreen ? 110 : 80,
            resizeMode: 'contain',
            aspectRatio: 220 / 80,
          }}
        />
        {isBigScreen && (
          <>
            <View style={style.navSeparator} />
            <Text>MyPaidy</Text>
          </>
        )}
      </View>
      <Text
        accessibilityRole="link"
        href={routes.unauthenticated.signIn}
        onPress={(event) => {
          event.preventDefault();
          navigate(routes.unauthenticated.signIn);
        }}
        style={[style.navText, style.textBold]}
        {...generateID('settlement-login-link')}
      >
        ログイン
      </Text>
    </View>
  );
};

export const Error = () => (
  <View style={style.errorWrapper}>
    <Text style={style.textCenter} {...generateID('settlement-error-message')}>
      本ページの有効期間は終了しています。{'\n'}
      ご請求はペイディアプリからご確認ください。
    </Text>
  </View>
);

const AutodebitOnly = () => (
  <View style={[style.dangerWrapper, style.marginBottom24]}>
    <Text
      style={[style.textCenter, style.textDanger]}
      {...generateID('settlement-autodebit-only-message')}
    >
      ご登録の銀行口座から自動的に引き落とされます。
    </Text>
  </View>
);

const Overdue = () => (
  <View style={[style.dangerWrapper, style.marginBottom24]}>
    <Text
      style={[style.textCenter, style.textDanger]}
      {...generateID('settlement-overdue-message')}
    >
      支払期限以降も入金が確認できておりません。{'\n'}
      行き違い等ありましたら、050-3155-1030までご連絡ください。
    </Text>
  </View>
);

const BillSettlementScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paidyCode } = useParams();
  const [method, setMethod] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [vanDataError, setVanDataError] = useState(false); // Happens when VAN data fails to load and shows fallback instead

  const [
    fetchBill,
    { data: billData, error },
  ] = usePublicBillStatementLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      paidyCode: paidyCode?.trim() || '',
    },
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  useEffect(() => {
    if (!location.state?.bill) {
      fetchBill();
    }
  }, []);

  // overdue bills 120+ days cannot be fetched with query billStatement(paidyCode: $paidyCode)
  // to prevent showing the error theOldest bill from BillsScreen is used to pass to this screen
  const data = location.state?.bill ?? billData;

  const isOverdue = data?.billStatement.status === BillStatementStatus.OVERDUE;
  const isPaid = data?.billStatement.status === BillStatementStatus.PAID;
  // If the bill is not yet PAID, use this. This amount changes for any user activities
  // e.g partially pay the bill or make refunds.
  // This automatically includes the lateFeeAmount if the bill becomes OVERDUE.
  const outstandingAmount = data?.billStatement.outstandingAmount || 0;

  // Unpaid amount is immutable. This number is created during the bill generation.
  // Do not use this if the bill is still unpaid, because users can choose to partially pay or make refunds.
  // Use this only when bill id PAID.
  const unpaidAmount = data?.billStatement.unpaidAmount || 0;

  // Use only alongside the unpaidAmount when bill is fully paid.
  // So PAID bills should show unpaidAmount + lateFeeAmount
  const lateFeeAmount = data?.billStatement.lateFeeAmount || 0;

  const isBankTransferAvailable =
    data?.billStatement.bankTransferSettlementAvailable || false;
  const isCombiniAvailable =
    data?.billStatement.combiniSettlementAvailable || false;
  const isAutodebitAvailable =
    data?.billStatement.autoDebitSettlement && !isOverdue;
  const dueAt = data?.billStatement.dueAt;
  const paidAt = data?.billStatement.paidAt;
  const isBankTransfer = method === 'transfer';
  // Assuming we get "s" query parameter that indicates the source only for this page.
  // "s=1" when we are from SMS billing notification.
  const sourceParam =
    new URLSearchParams(location.search).get('s') === '1' ? 'sms' : 'direct';

  useEffect(() => {
    Mixpanel.trackEvent(PAGE_VIEW_UNAUTHENTICATED_BILL, {
      source: sourceParam,
    });
  }, []);

  useEffect(() => {
    Mixpanel.timeEvent(TIMED_PAGE_VIEW_UNAUTHENTICATED_BILL);

    if (isPaid) {
      Mixpanel.timeEvent(PAGE_VIEW_UNAUTHENTICATED_PAID_BILL);
    }

    if (error) {
      Mixpanel.timeEvent(PAGE_VIEW_UNAUTHENTICATED_BILL_UNAVAILABLE);
    }

    return () => {
      Mixpanel.trackEvent(TIMED_PAGE_VIEW_UNAUTHENTICATED_BILL, {
        source: sourceParam,
      });

      if (isPaid) {
        Mixpanel.trackEvent(PAGE_VIEW_UNAUTHENTICATED_PAID_BILL);
      }

      if (error) {
        Mixpanel.trackEvent(PAGE_VIEW_UNAUTHENTICATED_BILL_UNAVAILABLE);
      }
    };
  }, [error, sourceParam, isPaid]);

  useEffect(() => {
    if (method && !isBankTransfer) {
      if (isDialogOpen) {
        Mixpanel.timeEvent(PAGE_VIEW_SETTLEMENT[method]);
      } else {
        Mixpanel.trackEvent(PAGE_VIEW_SETTLEMENT[method]);
      }
    }
  }, [method, isDialogOpen]);

  useEffect(() => {
    if (isBankTransfer) {
      if (isDialogOpen) {
        Mixpanel.timeEvent(PAGE_VIEW_SETTLEMENT[method]);

        if (vanDataError) {
          Mixpanel.timeEvent(PAGE_VIEW_VAN_ERROR);
        }
      } else {
        Mixpanel.trackEvent(PAGE_VIEW_SETTLEMENT[method]);

        if (vanDataError) {
          Mixpanel.trackEvent(PAGE_VIEW_VAN_ERROR);
        }
      }
    }
  }, [method, isDialogOpen, vanDataError]);

  return (
    <View style={style.root}>
      <NavigationBar />
      <View accessibilityRole="main" style={style.main}>
        <View style={style.container}>
          {error && <Error />}
          {data?.billStatement && (
            <>
              {isOverdue && <Overdue />}
              <LinearGradient
                colors={['#E5258C', '#823CD7']}
                /** will do an approximately 135-degree gradient */
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 6 }}
                style={[style.billWrapper, style.marginBottom24]}
              >
                <Text
                  style={[
                    style.billText,
                    style.textCenter,
                    style.marginBottom16,
                  ]}
                >
                  請求金額
                </Text>
                <Text
                  style={[
                    style.billText,
                    style.billAmountText,
                    style.textCenter,
                    style.textBold,
                    style.marginBottom16,
                  ]}
                  {...generateID('settlement-amount')}
                >
                  {formatToCurrency(
                    isPaid ? unpaidAmount + lateFeeAmount : outstandingAmount
                  )}
                </Text>
                <View style={[style.billSeparator, style.marginBottom16]} />
                {isOverdue ? (
                  <Text
                    style={[style.billText, style.textCenter]}
                    {...generateID('settlement-payment-date')}
                  >
                    支払期日 {formatYearMonthDate(dueAt)}
                    {'\n'}（期日より{Math.floor(getNumberOfDaysToNow(dueAt))}
                    日遅れています）
                  </Text>
                ) : (
                  <View style={style.flexHorizontal}>
                    <Text style={style.billText}>支払期日</Text>
                    <Text
                      style={style.billText}
                      {...generateID('settlement-payment-date')}
                    >
                      {isPaid
                        ? formatYearMonthDate(paidAt)
                        : formatYearMonthDate(dueAt)}
                    </Text>
                  </View>
                )}
              </LinearGradient>
              {!isPaid && (
                <>
                  {!data.billStatement.autoDebitSettlement &&
                    !isHighPriceBill(
                      data.billStatement as billStatementFragment
                    ) && (
                      <Text style={[style.textSecondary, style.marginBottom24]}>
                        ※上記の請求金額を支払期日
                        {formatYearMonthDate(dueAt)}
                        までにお支払いください。店舗により支払方法が異なりますので、各アイコンよりご確認ください。
                      </Text>
                    )}
                  {data.billStatement.combiniSettlementAvailable && (
                    <Text style={[style.textSecondary, style.marginBottom24]}>
                      {`※コンビニでご請求をお支払いいただく場合、1回につき最大税込${COMBINI_FEE}円の手数料がかかります。`}
                    </Text>
                  )}
                  <Text style={[style.textSecondary, style.marginBottom24]}>
                    ※本ページの有効期間は、ご請求確定後30日間です。
                  </Text>
                  <Text style={[style.textSecondary, style.marginBottom24]}>
                    ※ペイディアプリでは、ご請求確定後、120日間お支払いいただけます。また、毎月のご請求の履歴をすべて見ることができます。
                  </Text>
                  {isAutodebitAvailable && <AutodebitOnly />}
                  {isHighPriceBill(
                    data.billStatement as billStatementFragment
                  ) && (
                    <Text style={style.highPriceBillWarning}>
                      ご請求金額が299,000円を超えているため、コンビニ払いはご利用いただけません。お手数ですが、銀行振込にてお支払いください。
                    </Text>
                  )}
                  <SettlementMethods
                    isBankTransferAvailable={
                      isBankTransferAvailable && !isAutodebitAvailable
                    }
                    isCombiniAvailable={
                      isCombiniAvailable &&
                      !isAutodebitAvailable &&
                      !isHighPriceBill(
                        data.billStatement as billStatementFragment
                      )
                    }
                    onItemPress={(combini) => {
                      setMethod(combini);
                      setIsDialogOpen(true);
                    }}
                    style={style.marginBottom24}
                  />
                  {method && paidyCode && (
                    <Dialog
                      open={isDialogOpen}
                      title={
                        method === 'transfer'
                          ? 'お客様専用振込口座'
                          : (combiniData as any)[method].title
                      }
                      onCloseButtonPress={() => setIsDialogOpen(false)}
                      style={[
                        style.dialog,
                        isBankTransfer && { backgroundColor: '#EEF0F2' },
                      ]}
                      id="settlement-method-dialog"
                    >
                      {isBankTransfer ? (
                        <BankTransfer
                          paidyCode={paidyCode}
                          outstandingAmount={outstandingAmount}
                          onError={() => setVanDataError(true)}
                        />
                      ) : (
                        <CombiniSettlement
                          combini={method as keyof typeof combiniData}
                          paidyCode={paidyCode}
                          billId={data.billStatement.id}
                          style={style.combiniSettlement}
                        />
                      )}
                    </Dialog>
                  )}
                </>
              )}
              {isPaid && (
                <View style={style.successWrapper}>
                  <Image
                    source={{ uri: ImageCheck }}
                    style={[style.successImage, style.marginBottom24]}
                  />
                  <Text
                    style={[
                      style.successText,
                      style.textCenter,
                      style.textBold,
                    ]}
                    {...generateID('settlement-paid-message')}
                  >
                    お支払いありがとうございました
                  </Text>
                </View>
              )}
            </>
          )}
          {data?.billStatement && !isPaid && (
            <Text style={[style.textCenter, style.textSecondary]}>
              お支払い期日を超えますと手数料が加算されますのでご注意ください。
              {'\n'}
              ご利用明細につきましては
              <Text
                accessibilityRole="link"
                href={routes.unauthenticated.signIn}
                onPress={(event) => {
                  event.preventDefault();
                  navigate(routes.unauthenticated.signIn);
                }}
                style={[style.textSecondary, style.textLink]}
              >
                MyPaidy
              </Text>
              にログインしてご確認ください。
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: '#EEF0F2',
    flex: 1,
    position: 'relative',
  },
  flexHorizontal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  textDanger: {
    color: '#FF2751',
  },
  textSecondary: {
    color: '#586065',
    fontSize: 12,
  },
  textBold: {
    fontWeight: 'bold',
  },
  textLink: {
    color: '#0064d2',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  marginBottom24: {
    marginBottom: 24,
  },
  nav: {
    height: 64,
    backgroundColor: 'white',
    paddingHorizontal: 16,
  },
  navBigScreen: {
    height: 72,
  },
  navSeparator: {
    width: StyleSheet.hairlineWidth,
    height: 36,
    backgroundColor: '#CCCCCC',
    marginHorizontal: 16,
  },
  navText: {
    color: '#A1AAB0',
  },
  main: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
  },
  container: {
    width: '100%',
    maxWidth: 980,
  },
  errorWrapper: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 24,
  },
  dangerWrapper: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#FF2751',
    borderRadius: 8,
    padding: 24,
  },
  billWrapper: {
    padding: 24,
    width: '100%',
  },
  billText: {
    fontSize: 12,
    color: 'white',
  },
  billAmountText: {
    fontSize: 24,
  },
  billSeparator: {
    backgroundColor: '#EEF0F2',
    height: StyleSheet.hairlineWidth,
  },
  successWrapper: {
    alignItems: 'center',
  },
  successImage: {
    width: 128,
    height: 128,
  },
  successText: {
    fontSize: 12,
  },
  dialog: {
    maxWidth: 424,
  },
  combiniSettlement: {
    padding: 0,
    marginHorizontal: -24,
  },
  highPriceBillWarning: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '300',
    color: '#FF2751',
    borderWidth: 1,
    borderColor: '#FF2751',
    padding: 16,
    borderRadius: 8,
    marginBottom: 24,
    textAlign: 'center',
  },
});

export default BillSettlementScreen;
